import {Color} from '@benchling/nucleus-ui';
import {createStyles} from '@mantine/core';
import classNames from 'classnames';
import React from 'react';

import Menu, {MenuProps} from 'ui-library/menus/Menu';

import {AI_GRAY_CHEVRON} from './AIStyleConstants';

const AI_MENU_RADIUS = 16;
const AI_MENU_OPTION_RADIUS = 8;

export type AIMenuProps = Omit<
  MenuProps,
  | '_role'
  | 'className'
  | 'submenuClassName'
  | 'optionContainerClassName'
  | 'optionClassName'
  | 'optionContentClassName'
  | 'optionChildrenClassName'
  | 'optionMetadataClassName'
  | 'optionChevronClassName'
  | 'dividerClassName'
>;

const useAIMenuStyles = createStyles(() => ({
  rootMenu: {
    // Use `&&` so these selectors outrank matching `.menu...` rules in less/components/menus.less.
    '&&': {
      border: `1px solid ${Color.GRAY_300}`,
      borderRadius: AI_MENU_RADIUS,
      boxShadow: '0 3px 12px 0 rgb(0 0 0 / 6%)',
      margin: '4px 0',
      padding: '8px 0',
    },
    '&&.menu--largeWidth.menu': {
      width: 250,
    },
    '&&.menu--mediumWidth.menu': {
      width: 222,
    },
    // AI-chat-only horizontal nudge for xl dropdown menus.
    '&&.menu--xlWidth.menu': {
      left: '-12px',
    },
    '&& > .menu-body > li:first-child .menu-option, && > .menu-body > li:first-child .menu-option .menu-optionContent':
      {
        borderTopLeftRadius: AI_MENU_OPTION_RADIUS,
        borderTopRightRadius: AI_MENU_OPTION_RADIUS,
      },
    '&& > .menu-body > li:last-child .menu-option, && > .menu-body > li:last-child .menu-option .menu-optionContent':
      {
        borderBottomLeftRadius: AI_MENU_OPTION_RADIUS,
        borderBottomRightRadius: AI_MENU_OPTION_RADIUS,
      },
    '&&.menu.menu--single .menu-option.is-selected, &&.menu.menu--single .menu-option.is-selected:hover, &&.menu.menu--single .menu-option.is-selected .menu-optionContent, &&.menu.menu--single .menu-option.is-selected:hover .menu-optionContent':
      {
        backgroundColor: 'transparent',
      },
    '&&.menu .menu-option.is-uncontrolled-hover:focus:not(:hover), &&.menu .menu-option.is-uncontrolled-hover:focus:not(:hover) .menu-optionContent':
      {
        backgroundColor: 'transparent',
      },
  },
  submenu: {
    // Keep a visible gap between parent AI menu and nested submenu.
    '&&.menu.menu--submenu': {
      marginLeft: -10,
    },
    '&&.menu.menu--submenu.menu--dropLeft': {
      marginLeft: 0,
      marginRight: 5,
    },
    '&&.menu.menu--submenu.menu--dropup': {
      borderBottomLeftRadius: AI_MENU_RADIUS,
      borderTopLeftRadius: AI_MENU_RADIUS,
      borderTopRightRadius: AI_MENU_RADIUS,
      borderBottomRightRadius: AI_MENU_RADIUS,
    },
    '&&.menu.menu--submenu.menu--dropLeft.menu--dropup': {
      borderTopLeftRadius: AI_MENU_RADIUS,
      borderTopRightRadius: AI_MENU_RADIUS,
      borderBottomLeftRadius: AI_MENU_RADIUS,
      borderBottomRightRadius: 0,
    },
  },
  option: {
    '&&.menu-option': {
      backgroundColor: 'transparent',
      borderRadius: AI_MENU_OPTION_RADIUS,
      margin: '0 6px',
    },
    '&&.menu-option.is-selected, &&.menu-option.is-selected:hover': {
      color: Color.GRAY_900,
    },
  },
  optionContent: {
    '&&.menu-optionContent': {
      borderRadius: AI_MENU_OPTION_RADIUS,
      padding: '0 10px',
    },
  },
  optionChildren: {
    '&&.menu-option-children': {
      padding: '8px 0 6px',
    },
  },
  optionMetadata: {
    '&&.menu-option-metadata': {
      color: Color.GRAY_500,
      fontSize: 13,
      lineHeight: '16px',
      paddingTop: 2,
      paddingBottom: 2,
    },
  },
  optionChevron: {
    '&&.menu-option-chevron': {
      color: AI_GRAY_CHEVRON,
    },
  },
  divider: {
    '&&.menu-divider': {
      backgroundColor: Color.GRAY_200,
      height: 1,
      margin: '6px 0',
      overflow: 'hidden',
    },
  },
}));

/**
 * AI-styled wrapper around Menu.
 *
 * Use AIMenu only at the root of a menu hierarchy.
 * Nested submenus should use regular Menu.
 */
export default function AIMenu(props: AIMenuProps): JSX.Element {
  const {classes} = useAIMenuStyles();
  return (
    <Menu
      {...props}
      className={classNames(classes.rootMenu)}
      submenuClassName={classNames(classes.submenu)}
      optionClassName={classNames(classes.option)}
      optionContentClassName={classNames(classes.optionContent)}
      optionChildrenClassName={classNames(classes.optionChildren)}
      optionMetadataClassName={classNames(classes.optionMetadata)}
      optionChevronClassName={classNames(classes.optionChevron)}
      dividerClassName={classNames(classes.divider)}
    />
  );
}
